class BundleCartItem extends HTMLElement {
  constructor() {
    super();
    try {
      this.components = JSON.parse(this.dataset.componentData);

      /**
       * @type {ShopifyProduct[]}
       */
      this.componentData = [];
      this.spinner = this.querySelector('.spinner');
    } catch (error) {
      console.error('Error parsing component data', error);
    }
  }

  /**
   * @type {number}
   */
  cacheExpirationTime = 1000 * 60 * 2; // 2 minutes

  compressString(input) {
    const dictionary = {};
    const data = (input + '').split('');
    const out = [];
    let currChar;
    let phrase = data[0];
    let code = 256;
    for (let i = 1; i < data.length; i++) {
      currChar = data[i];
      if (dictionary[phrase + currChar] != null) {
        phrase += currChar;
      } else {
        out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
        dictionary[phrase + currChar] = code;
        code++;
        phrase = currChar;
      }
    }
    out.push(phrase.length > 1 ? dictionary[phrase] : phrase.charCodeAt(0));
    for (let i = 0; i < out.length; i++) {
      out[i] = String.fromCharCode(out[i]);
    }
    return out.join('');
  }

  async fetchComponentData() {
    const variantIds = this.compressString(Object.keys(this.components).join(','));
    const country = window.Shopify.country;

    const compressedParam = encodeURIComponent(variantIds);

    // Use the same cache key format as other components
    const cacheKey = `wmxb_bcd_${compressedParam}_${country}`;
    const inProgressKey = `${cacheKey}_in_progress`;

    // Check if we have cached data without expiration
    const cachedData = sessionStorage.getItem(cacheKey);

    // Check if there's a request in progress
    const inProgress = window[inProgressKey];

    if (cachedData) {
      // Use cached data if available (no expiration check for cart items)
      this.componentData = JSON.parse(cachedData);
      this.renderComponentData();
      return;
    }

    // If there's a request in progress, wait for it to complete
    if (inProgress) {
      await this.waitForCachedData(cacheKey, inProgressKey);
      return;
    }

    // Mark this request as in progress
    window[inProgressKey] = true;

    try {
      // Fetch new data if not in cache
      const response = await fetch(`/a/wmxb/bcd?c=${variantIds}&country=${country}`);
      const data = await response.json();

      // Cache the response (no expiration for cart items)
      sessionStorage.setItem(cacheKey, JSON.stringify(data));

      // Set expiration time (2 minutes) for other components to use
      const expiration = Date.now() + this.cacheExpirationTime;
      sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());

      this.componentData = data;
      this.renderComponentData();
    } catch (error) {
      console.error('Error fetching bundle component data:', error);
    } finally {
      // Remove the in-progress flag
      window[inProgressKey] = false;
    }
  }

  /**
   * Wait for cached data to be available
   * @param {string} cacheKey - The cache key to check
   * @param {string} inProgressKey - The in-progress key to check
   * @param {number} maxWaitTime - Maximum wait time in milliseconds
   * @param {number} checkInterval - Interval between checks in milliseconds
   * @returns {Promise<void>}
   */
  async waitForCachedData(cacheKey, inProgressKey, maxWaitTime = 5000, checkInterval = 100) {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      // Check if the data is now available
      const cachedData = sessionStorage.getItem(cacheKey);
      const stillInProgress = window[inProgressKey];

      if (cachedData) {
        // Data is available, use it
        this.componentData = JSON.parse(cachedData);
        this.renderComponentData();
        return;
      }

      if (!stillInProgress) {
        // The request is no longer in progress but data is not available
        // This likely means the request failed
        break;
      }

      // Wait before checking again
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }

    // If we get here, either the wait timed out or the request failed
    // Proceed with our own request
    try {
      const variantIds = this.compressString(Object.keys(this.components).join(','));
      const country = window.Shopify.country;
      window[inProgressKey] = true;
      const response = await fetch(`/a/wmxb/bcd?c=${variantIds}&country=${country}`);
      const data = await response.json();

      // Cache the response
      sessionStorage.setItem(cacheKey, JSON.stringify(data));

      // Set expiration time (2 minutes)
      const expiration = Date.now() + this.cacheExpirationTime;
      sessionStorage.setItem(`${cacheKey}_expiration`, expiration.toString());

      this.componentData = data;
      this.renderComponentData();
    } catch (error) {
      console.error('Error fetching bundle component data after waiting:', error);
    } finally {
      // Remove the in-progress flag
      window[inProgressKey] = false;
    }
  }

  renderComponentData() {
    const container = document.createElement('div');
    container.classList.add('bundle-cart-item');

    this.componentData.forEach((product) => {
      const productContainer = document.createElement('div');
      productContainer.classList.add('product-container');

      const componentTitle = document.createElement('span');
      componentTitle.style.fontSize = '1.4rem';
      componentTitle.style.fontWeight = 'normal';
      componentTitle.classList.add('component-title');

      const validOptions = product.variants[0].selectedOptions.filter((option) => option.value !== 'Default Title');
      const optionsText = validOptions.map((option) => option.value).join(' / ');
      const componentTitleText = `${product.title}${optionsText ? ` - ${optionsText}` : ''}`;

      componentTitle.textContent = componentTitleText;

      productContainer.appendChild(componentTitle);
      container.appendChild(productContainer);
    });

    this.appendChild(container);
    this.spinner.classList.add('hidden');
  }

  connectedCallback() {
    this.fetchComponentData();
  }
}

customElements.define('bundle-cart-item', BundleCartItem);

/**
 * @typedef {Object} ShopifyProduct
 * @property {string} id
 * @property {string} title
 * @property {string} handle
 * @property {string[]} tags
 * @property {number} minimumPrice
 * @property {Options} options
 * @property {null} preOrderReleaseDate
 * @property {Media} media
 * @property {string} vendor
 * @property {string} __typename
 * @property {ShopifyProductVariant[]} variants
 */

/**
 * @typedef {Object} Options
 * @property {OptionEdge[]} edges
 */

/**
 * @typedef {Object} OptionEdge
 * @property {OptionNode} node
 */

/**
 * @typedef {Object} OptionNode
 * @property {string} name
 * @property {number} position
 * @property {string[]} values
 */

/**
 * @typedef {Object} Media
 * @property {MediaEdge[]} edges
 */

/**
 * @typedef {Object} MediaEdge
 * @property {MediaNode} node
 */

/**
 * @typedef {Object} MediaNode
 * @property {Image} image
 */

/**
 * @typedef {Object} Image
 * @property {string} id
 * @property {number} width
 * @property {number} height
 * @property {string} originalSrc
 */

/**
 * @typedef {Object} ShopifyProductVariant
 * @property {string} id
 * @property {string} title
 * @property {Media} media
 * @property {number} inventoryQuantity
 * @property {string} price
 * @property {null} compareAtPrice
 * @property {null} preorderStock
 * @property {string} productId
 * @property {SelectedOption[]} selectedOptions
 * @property {string} __typename
 */

/**
 * @typedef {Object} SelectedOption
 * @property {string} name
 * @property {string} value
 * @property {OptionValue} optionValue
 */

/**
 * @typedef {Object} OptionValue
 * @property {string} id
 * @property {string} name
 * @property {null} swatch
 * @property {boolean} hasVariants
 * @property {null} linkedMetafieldValue
 */
